/**
 * «پیش‌پرداخت مراجعه» — money held FOR one appointment (PM-0657, contract §6.1).
 *
 * One rule, one file: the amount the desk is offered, what the reception row
 * says about a deposit, and whether the refund window is still open. Every
 * surface (the booking dialog, the row chip, the deposit dialog, the cancel /
 * no-show modal, the settle drawer) reads these and never works them out again.
 *
 * MONEY: minor units of the deposit's currency, like everything on the wire.
 * TIME: the refund deadline is an INSTANT (epoch ms, computed by the BFF as the
 * appointment's instant minus the snapshot window in hours) — no wall clock or
 * zone enters the comparison. Only its DISPLAY is on the clinic's zone.
 */

/** The flows on which a deposit can still be taken / is still expected. */
export const DEPOSIT_OPEN_FLOWS = ['scheduled', 'arrived', 'in_visit'];

/**
 * The amount «ثبت نوبت» prefills. Mirrors the policy's three modes:
 * SERVICE_PRICE → the booked service's full tariff (Q8); PERCENT → that share,
 * rounded half up to a whole minor unit; FIXED → the fixed amount. OFF, no
 * policy, no price, or a FIXED amount cleared to 0 by a currency change → null
 * (nothing is suggested; the field stays empty).
 */
export function suggestedDepositRial(policy, priceRial) {
  if (!policy || policy.mode === 'OFF') return null;
  if (policy.amountMode === 'FIXED') {
    const fixed = Number(policy.fixedAmountRial) || 0;
    return fixed > 0 ? fixed : null;
  }
  const price = Number(priceRial);
  if (!Number.isFinite(price) || price <= 0) return null;
  if (policy.amountMode === 'PERCENT') {
    const pct = Number(policy.percent) || 0;
    return Math.floor((price * pct + 50) / 100);
  }
  return price;
}

/**
 * The reception row's chip: `held | applied | released | refunded | forfeited |
 * missing | null`. `missing` (amber) only when the clinic made the deposit
 * REQUIRED and the row still stands before the visit (C9: a warning, never a
 * refusal).
 */
export function depositChip(deposit, mode, flow) {
  if (!hasDepositMoney(deposit)) {
    return mode === 'REQUIRED' && (flow === 'scheduled' || flow === 'arrived') ? 'missing' : null;
  }
  if (deposit.state === 'HELD') return 'held';
  if ((Number(deposit.forfeitedRial) || 0) > 0) return 'forfeited';
  const deposited = Number(deposit.depositedRial) || 0;
  if (deposit.closeReason === 'REFUNDED'
    || (deposited > 0 && (Number(deposit.refundedRial) || 0) >= deposited)) return 'refunded';
  if (deposit.closeReason === 'APPLIED') return 'applied';
  return 'released';
}

/**
 * Whether a deposit ever took money. Accounting opens the hold in its own commit
 * before the first deposit row, so a deposit refused after that (the invoice
 * issued under the lock, a currency or patient mismatch) leaves an EMPTY hold —
 * open or closed. That is no deposit, as the patient BFF already reads it. A
 * read that did not select `depositedRial` is taken at its state.
 */
export function hasDepositMoney(deposit) {
  if (!deposit) return false;
  if (deposit.depositedRial == null) return true;
  return (Number(deposit.depositedRial) || 0) > 0;
}

/** The deadline as a number, or null when the appointment's time is unknown. */
export function refundDeadlineMs(deposit) {
  const raw = deposit?.refundDeadline;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Whether cancelling NOW may still hand the deposit back in one click (D4, Q7). */
export function refundAllowed(deposit, now = Date.now()) {
  const deadline = refundDeadlineMs(deposit);
  return deadline != null && now <= deadline;
}

/** What a deposit still holds for its مراجعه — 0 unless it is HELD. */
export function heldDepositOf(deposit) {
  return deposit?.state === 'HELD' ? Math.max(0, Number(deposit.heldRial) || 0) : 0;
}

/**
 * An UNISSUED invoice's due once the held deposit is set against it — the figure
 * the server lets the desk collect before issue (C7). Floors at zero.
 */
export function netOfHeldDeposit(due, held) {
  return Math.max(0, (Number(due) || 0) - (Number(held) || 0));
}

/** The ids of the credit payments the deposit settled with (row.paymentId). */
export function depositPaymentIds(deposit) {
  const ids = new Set();
  for (const row of deposit?.rows || []) if (row?.paymentId) ids.add(row.paymentId);
  return ids;
}

/** Wire fields every read of a deposit asks for. */
export const DEPOSIT_ROW_FIELDS = `
  state closeReason heldRial depositedRial appliedRial refundedRial forfeitedRial releasedRial
  refundableRial refundDeadline refundWindowHours noShowOutcome noShowFeePercent currency
`;
